import { css } from '@emotion/react'
import { Button, HeadingSection, Icon, Modal, ModalBody, ModalHeader, Text } from 'bold-ui'
import { Form, FormRenderProps } from 'components/form'
import { confirm, ConfirmConfigCustomRender } from 'components/modals/confirm'
import { Decorator } from 'final-form'
import { ReactNode, useMemo, useState } from 'react'
import { ValidatorFunction } from 'util/validation'

import { RequisicaoExameModalFooter } from './RequisicaoExameModalFooter'
import { RequisicaoExameModalSidebar } from './RequisicaoExameModalSidebar'

interface RequisicaoExameModalProps<TFormValues> {
  onClose: () => void
  onSubmit: (values: TFormValues) => void
  title: string
  steps: RequisicaoExameModalStep<TFormValues>[]
  initialValues?: Partial<TFormValues>
  decorators: Decorator<TFormValues>[]
}

export type RequisicaoExameModalStep<TFormValues> = {
  title: string
  render: () => ReactNode
  validator: ValidatorFunction<TFormValues>
}

export function RequisicaoExameModal<TFormValues extends object>(
  props: Readonly<RequisicaoExameModalProps<TFormValues>>
) {
  const { onClose, onSubmit, title, steps, initialValues, decorators } = props

  const [activeStepIdx, setActiveStepIdx] = useState<number>(0)
  const [completedStepsIdx, setCompletedStepsIdx] = useState<number[]>([])

  const activeStep = steps[activeStepIdx]
  const isLastStep = activeStepIdx === steps.length - 1

  const handleClose = (isDirty: boolean) => {
    if (isDirty) {
      confirm({
        ...closeModalConfigs,
        onConfirm: onClose,
      })()
    } else {
      onClose()
    }
  }

  const onContinue = () => {
    setCompletedStepsIdx((prev) => [...prev, activeStepIdx])
    setActiveStepIdx((prev) => prev + 1)
  }

  const handleGoBack = () => {
    setActiveStepIdx((prev) => prev - 1)
  }

  const handleFormSubmit = (values: TFormValues) => {
    if (isLastStep) {
      onSubmit(values)
    } else {
      onContinue()
    }
  }

  const stepTitles = useMemo(() => steps.map((s) => s.title), [steps])

  const renderModal = (renderProps: FormRenderProps<TFormValues>) => (
    <Modal open size='large' closeOnBackdropClick={false} onClose={() => handleClose(renderProps?.dirty)}>
      <ModalHeader title={title} />
      <RequisicaoExameModalSidebar
        activeStepIdx={activeStepIdx}
        completedStepsIdx={completedStepsIdx}
        steps={stepTitles}
      />
      <ModalBody>
        {activeStepIdx > 0 && (
          <Button size='small' onClick={handleGoBack} style={styles.backButton}>
            <Icon icon='arrowLeft' size={1.2} style={styles.backButtonIcon} />
            Voltar
          </Button>
        )}
        <HeadingSection level={2} title={activeStep.title}>
          {activeStep.render()}
        </HeadingSection>
      </ModalBody>
      <RequisicaoExameModalFooter
        onClose={() => handleClose(renderProps?.dirty)}
        onSubmit={renderProps.handleSubmit}
        isLastStep={isLastStep}
      />
    </Modal>
  )

  return (
    <Form<TFormValues>
      initialValues={(initialValues ?? {}) as TFormValues}
      validate={activeStep.validator}
      onSubmit={handleFormSubmit}
      render={renderModal}
      decorators={decorators}
    />
  )
}

const closeModalConfigs: ConfirmConfigCustomRender = {
  title: 'Deseja cancelar a requisição?',
  icon: {
    name: 'questionMarkOutline',
    fill: 'secondary',
  },
  confirmLabel: 'Sim, cancelar',
  cancelLabel: 'Não, continuar aqui',
  type: 'primary',
  size: 'auto',
  depthLevel: 2,
  render: () => <Text>As alterações realizadas serão perdidas</Text>,
}

const styles = {
  backButtonIcon: css`
    margin-right: 0.25rem;
  `,
  backButton: css`
    margin-bottom: 1rem;
  `,
}
